import { IStationInfo } from "../dto";
import {
  CourseRouteTypeEnum,
  CourseSettingsSourceEnum,
  DirectionEnum,
  DirectionsServiceEnum,
  DragTypeStationEnum,
  FixCourseMergingMethodEnum,
  FixCourseRebuildRideOptionEnum,
  RebuildRouteTypeEnum,
  WaypointCategoryEnum,
} from "../enums";
import { ILocationInfo } from "./common.models";
import { TTimeHHmm } from "./date-time.models";
import {
  ISortableData,
  TDraggingStation,
  TNoDrag,
  TNotDragging,
} from "./rides.drag.models";

export interface IRouteSchedule {
  time: TTimeHHmm;
  direction: string;
  dayTypeIds: number[];
  dayGroupIds: number[];
}

export interface ICourseStationInfo {
  id: number;
  name: string;
  locationInfo: ILocationInfo;
  isLocked?: boolean;
  comment?: string;
  timeArrival?: TTimeHHmm | "" /* HH:mm format for time*/;
  areaId?: number;
  seatsCountByDriver?: number;
  realArrivalTime?: TDateISO;
  /** Routing-only map point — not a passenger station. */
  waypointCategory?: WaypointCategoryEnum;
}

export interface ICourseMetadata {
  // not really needed, here to ease on searches by id
  areaIds?: number[];
  innerAreaIds?: number[];
  stationIds?: number[];
  pickupStationsIds?: number[];
  dropoffStationsIds?: number[];
  passengerTypeIds?: number[];
  schedule?: IRouteSchedule[];
  availabilityInfo?: string;
  productionMaxPassengers?: string;
  productionMinPassengers?: string;
  combineMaxPassengers?: string;
  combineMinPassengers?: string;
  routeIdMerged?: string;
  durationStation?: string;
  durationPassenger?: string;
  isJumps?: string;
  clientCode?: string;
  fixedPrice?: number;
  manualPrice?: number;
  totalDuration?: number;
  totalDistance?: number;
  tags?: string[];
  remarks?: string;
  vehicleTypeId?: number;
  distances?: number[];
  durations?: number[];
  stationsInfo?: ICourseStationInfo[];
  isActiveInProduction?: boolean;
  tollRoads?: boolean;
  settingsSource?: CourseSettingsSourceEnum;
  shiftIds?: number;
  priceRateIds?: number[];
  routePolyline?: string;
  mergingMethod?: FixCourseMergingMethodEnum;
  rebuildRideOption?: FixCourseRebuildRideOptionEnum;
  isManualPolylineEdit?: boolean;
  // TODO - is used in transformer but shouldn't exist in course according to Amir
  additionIds?: number[];
  passengerIds?: (number | undefined)[];
  direction?: DirectionEnum;
  /** Last routing engine used to compute the route (hidden in UI when absent on legacy courses). */
  directionsService?: DirectionsServiceEnum;
  /** Route includes routing map waypoints between passenger stations. */
  hasWaypoints?: boolean;
}
export interface ICourseDto {
  id: number;
  companyId: number;
  oldId: number;
  siteId?: number;
  supplierId?: number;
  driverId?: number;
  vehicleId?: number;
  routeNumber?: string;
  isActive?: boolean;
  name?: string;
  description?: string;
  pickupHours?: TTimeHHmm;
  dropoffHours?: TTimeHHmm;
  routeType?: CourseRouteTypeEnum;
  routeNumberMerged?: string;
  metadata?: ICourseMetadata;
  createdAt: TDateISO;
  updatedAt?: TDateISO;
  deletedAt?: Date;
  supplierName?: string;
  driverName?: string;
  vehicleTypeName?: string;
}

export interface IStationCourseProps {
  station: ICourseStationInfo;
  courseDto: ICourseDto;
  stationIndex: number;
  isLastStation: boolean;
  isFirstStation: boolean;
  isDragClone?: boolean;
}

export interface IStationWrapperCourseProps {
  stationId: number;
  courseDto: ICourseDto;
  stationIndex: number;
  isLastStation: boolean;
  isFirstStation: boolean;
  dragDropStationHandler?: (sortableUpdatedIds: number[]) => void;
}

export type TDragDropCourseSortable = {
  type: DragTypeStationEnum.STATION;
  sortable: ISortableData<number>;
  payload: IStationCourseProps;
};

export type TDraggingCourseStationPayload = TNotDragging | TDraggingStation;
export type TDragCourseStation = {
  type: DragTypeStationEnum.STATION;
  payload: IStationCourseProps;
};

export type TDragCourseData = TNoDrag | TDragCourseStation;

export type TDropCourseData = {
  courseDrop?: ICourseDto;
  accepts?: DragTypeStationEnum[];
  typeDrop: DragTypeStationEnum;
};

export class RecountCourseStationsCriteriaDto {
  stations: IStationInfo[];
  optimize = true;
  avoidTolls?: boolean;
  direction?: DirectionEnum; // currently unused, added for consistence with getRoute, to use it if needed
  provider: DirectionsServiceEnum;
  rebuildType?: RebuildRouteTypeEnum;
  constructor(constructorData?: Partial<RecountCourseStationsCriteriaDto>) {
    Object.assign(this, constructorData);
  }
}

export interface IUpdateCourseMetadataCriteria {
  metadata: ICourseMetadata;
  optimize?: boolean;
  skipApiCheck?: boolean;
  /** When set, live recount uses this rebuild type (BE owns cache/ML decisions for waypoints). */
  rebuildType?: RebuildRouteTypeEnum;
  skipReorderStationsByTime?: boolean;
}
